import { api } from '@/lib/api-client'
import { projectDashboardStats, projectResultList } from '@/domain/results/candidate-view'
import type {
  DashboardRecommendationsResponse,
  StudentDashboardStats,
  TestDefinition,
  TestListParams,
  TestListResponse,
  TestResultListResponse,
  UserProfile,
} from '@/lib/types'

export interface AuthResponse {
  profile: UserProfile
  /** Legacy backend name; contains the refresh JWT used by the current WebSocket. */
  auth_token: string
  /** Some environments return this camel-case alias; current student login does not. */
  refreshToken?: string
}

export interface CurrentUserSummary {
  id: string
  email: string
  full_name: string
}

export type LoginMode = 'student' | 'admin'

/**
 * The only social provider V2 offers.
 *
 * `mocktest/views/auth.py::social_auth` also accepts `facebook`, but that
 * branch reads `email`, `name` and `social_id` straight from the request body
 * with no token verification — anybody who can reach the endpoint can sign in
 * as anybody. It is not reimplemented here; see VUE-RETIREMENT GAP-2.
 */
export type SocialProvider = 'google'

export interface MessageResponse {
  message: string
}

export interface TokenValidationResponse {
  valid: boolean
}

export interface SuccessResponse {
  success: boolean
}

export type ReadingVariant = 'academic' | 'general_training'

export interface RawBandCalculation {
  calculator: 'raw_score'
  skill: 'listening' | 'reading'
  reading_variant: ReadingVariant | null
  raw_score: number
  scale_max: 40
  estimated_band: number
  table: 'listening' | 'reading_academic' | 'reading_general'
  source: 'published_practice_table' | 'edvantage_unpublished_low_score_floor'
  published_table_range: boolean
  explanation: string
}

export interface OverallBandInputs {
  listening: number
  reading: number
  writing: number
  speaking: number
}

export interface OverallBandCalculation {
  calculator: 'overall_band'
  section_bands: OverallBandInputs
  average: number
  overall_band: number
  rounding_rule: 'nearest_half_band_quarters_up'
  explanation: string
}

export const authApi = {
  login: (email: string, password: string, mode: LoginMode = 'student') =>
    api.post<AuthResponse>(mode === 'admin' ? '/admin/auth/login/' : '/auth/login/', { email, password }),
  register: (name: string, email: string, password: string) =>
    api.post<MessageResponse>('/auth/signup/', { name, email, password }),
  forgotPassword: (email: string) =>
    api.post<MessageResponse>('/auth/forgot-password/', { email }),
  validateResetToken: (token: string) =>
    api.post<TokenValidationResponse>('/auth/reset-password/validate/', { token }),
  resetPassword: (token: string, newPassword: string) =>
    api.post<SuccessResponse>('/auth/reset-password/', { token, new_password: newPassword }),
  activate: (token: string) => api.post<SuccessResponse>('/auth/activate/', { token }),
  /**
   * Google sign-in. The browser sends only the GSI credential; Django verifies
   * it against `settings.GOOGLE_CLIENT_ID` and sources the email, name and
   * `sub` from the VERIFIED payload, never from this body. The response is the
   * same `{ profile, auth_token }` shape as password login, so the session is
   * established identically.
   */
  socialAuth: (idToken: string, provider: SocialProvider = 'google') =>
    api.post<AuthResponse>('/auth/social/', { provider, id_token: idToken }),
  currentUser: () => api.get<CurrentUserSummary>('/auth/me/'),
  logout: (isAdmin = false) => api.post<void>(isAdmin ? '/admin/auth/logout/' : '/auth/logout/'),
}

export const testsApi = {
  /**
   * The candidate-facing library. Always asks for published tests only.
   *
   * The API hides drafts from students by role (`if not is_employee`), so a
   * signed-in staff member browsing this page would otherwise be shown the
   * whole catalogue - including half-authored drafts with placeholder
   * passages. That makes staff testing unrepresentative of what a candidate
   * sees, and it is how a stub like `<p>Passage</p>` ends up looking like a
   * broken live test. Drafts belong in the admin exam list, which asks for
   * them explicitly.
   */
  list: (params: TestListParams = { page: 1, limit: 12 }) => {
    const query = new URLSearchParams({
      page: String(params.page),
      limit: String(params.limit),
      status: 'active',
    })
    const search = params.search?.trim()
    if (search) query.set('search', search)
    if (params.test_type) query.set('test_type', params.test_type)
    if (params.year !== undefined) query.set('year', String(params.year))
    if (params.month !== undefined) query.set('month', String(params.month))
    if (params.available_only) query.set('available_only', 'true')
    if (params.hide_completed) query.set('hide_completed', 'true')
    if (params.ielts_module) query.set('ielts_module', params.ielts_module)
    return api.get<TestListResponse>(`/tests/?${query.toString()}`)
  },
  detail: (id: string) => api.get<TestDefinition>(`/tests/${id}`),
  /** Admin preview definition (includes unpublished drafts); employee cookies. */
  adminDetail: (id: string) => api.get<TestDefinition>(`/admin/tests/${id}/`),
  results: async () => projectResultList(await api.get<TestResultListResponse>('/tests/results/?limit=12&offset=0')),
}

export const dashboardApi = {
  stats: async () => projectDashboardStats(await api.get<StudentDashboardStats>('/dashboard/analytics/')),
  recommendations: (limit = 4) =>
    api.get<DashboardRecommendationsResponse>(`/dashboard/recommendations/?limit=${limit}`),
}

export const calculatorsApi = {
  rawBand: (skill: 'listening' | 'reading', rawScore: number, readingVariant?: ReadingVariant) => {
    const query = new URLSearchParams({ skill, raw_score: String(rawScore) })
    if (readingVariant) query.set('reading_variant', readingVariant)
    return api.get<RawBandCalculation>(`/calculators/raw-band/?${query.toString()}`)
  },
  overallBand: (bands: OverallBandInputs) => {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(bands).map(([key, value]) => [key, String(value)])),
    )
    return api.get<OverallBandCalculation>(`/calculators/overall-band/?${query.toString()}`)
  },
}
